import { call, delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { setEditAccount } from '../../containers/AccountPage/actions';
import {accountsChanged, getAccounts, getProms, setSelectedAccount} from '../../containers/AdminPage/actions';
import {selectAccounts} from '../../containers/AdminPage/selectors';
import { UPDATE_ACCOUNT } from '../constants/account.constants';
import { updateAccountFailed } from '../actions/account.actions';
import { AdminAPI } from '../../utils/API/AdminAPI';
import { LOGIN_ACCOUNT } from '../../containers/AccountPage/constants';
import { selectCurrentUser } from '../selectors/app.selectors';
import * as Sentry from "@sentry/browser";
import {enqueueSnackbar, userChanged} from "../actions/app.actions";
import {getI18n} from "react-i18next";
import Account from "../../utils/models/Account";
import v4api, {enterV4SessionScope, getV4Permissions, getV4SessionScope, v4ScopeForUuid, v4Unscoped} from "../../api/V4client";
import {applyV4Permissions, V4_ADMIN_RULES} from "../../containers/V4Users/permissions";
import {hasV4AccountRules, namesV4Account, withV4AccountRules} from "../../utils/v4Permissions";
import {buildV4AccountTree, isV4ManagerFlagged, V4_ACCOUNT_MANAGER_SETTING, v4ManagerSettingValue} from "../../utils/v4AccountTree";
import {v4BillingUpdate} from "../../utils/v4AccountBilling";
import {withV4AccountProfile} from "../../utils/v4AccountProfile";
import {fetchStoredV4Whitelabel} from "../../utils/v4AccountWhitelabel";

const api = new AdminAPI();

// V4's read model lags its writes, so a list read straight after a create can
// miss the new row.
const V4_LOOKUP_ATTEMPTS = 3;
const V4_LOOKUP_DELAY = 600;

// Unscoped: an entered scope answers with its parent and siblings, never the new row.
function* listAllV4Accounts() {
	return (yield v4api.accountsList(undefined, v4Unscoped()))?.data || [];
}

const createdRow = row => ({id: row.id, owner_id: row.owner_id || null});

/**
 * The account `accountsCreate` just made, as `{id, owner_id}`. `POST /v1/accounts`
 * declares no response body, so the row is read from it when present and
 * otherwise recovered from the account list — matching on BOTH "absent a moment
 * ago" and the requested name, since counting new rows alone would happily
 * return an account another manager created in the same window.
 */
function* createdV4Account(res, idsBefore, name) {
	const fromBody = res?.data?.id ? res.data : res?.data?.account?.id ? res.data.account : null;
	if (fromBody) return createdRow(fromBody);
	const wanted = String(name || '').trim().lc();
	for (let attempt = 0; attempt < V4_LOOKUP_ATTEMPTS; attempt++) {
		if (attempt) yield delay(V4_LOOKUP_DELAY);
		try {
			const list = yield* listAllV4Accounts();
			const fresh = list.filter(a => a?.id
				&& !a.deleted_at
				&& !idsBefore.has(String(a.id).lc())
				&& String(a.name || '').trim().lc() === wanted);
			if (fresh.length === 1) return createdRow(fresh[0]);
			// Ambiguous however long we wait.
			if (fresh.length > 1) return null;
		} catch (e) {
			console.warn('Could not resolve the new V4 account', e?.response?.data || e);
			return null;
		}
	}
	return null;
}

/**
 * Full access to the new account for each user picked in the create form.
 * Returns the grants that failed: a shortfall to report, and the answer to
 * whether this session's own rights changed.
 */
function* grantNewAccountPermissions(accountId, userIds) {
	if (!accountId || !userIds?.length) return [];
	const failed = yield call(applyV4Permissions, userIds.map(userId => ({
		accountId,
		userId,
		rules: V4_ADMIN_RULES,
	})));
	if (failed.length) {
		console.warn('Failed to grant V4 permissions on the new account',
			failed.map(f => [f.userId, f.reason]));
	}
	return failed;
}

// Re-selected each time: fetchV4AccountSettings can re-put the user mid-reconcile.
function* putV4Permissions(permissions) {
	const user = yield select(selectCurrentUser());
	if (user?.isV4Native) yield put(userChanged(user.set('v4Permissions', permissions)));
}

/**
 * `bootstrapV4Native` reads the permission map once per page load, so every gate
 * keyed on the new account — the Add-device button, its account picker — denies
 * until a manual refresh. Write the grant in now; `reconcileV4Permissions`
 * settles it. Returns whether there is anything to settle.
 */
function* applyV4SessionGrant(accountId, userIds, failed) {
	const user = yield select(selectCurrentUser());
	if (!user?.isV4Native || !accountId) return false;

	// `/v1/permissions` answers for this user alone, so only our own grant counts.
	const ownId = user.get('v4UserId') ? String(user.get('v4UserId')).lc() : null;
	const grantedSelf = !!ownId
		&& (userIds || []).some(id => String(id).lc() === ownId)
		&& !(failed || []).some(f => String(f.userId).lc() === ownId);
	if (!grantedSelf) return false;

	const permissions = user.get('v4Permissions');
	const optimistic = withV4AccountRules(permissions, accountId, V4_ADMIN_RULES);
	if (optimistic !== permissions) yield* putV4Permissions(optimistic);
	return true;
}

/**
 * Settles the optimistic grant against the server, after the dialog has closed —
 * its retries cost the user nothing. The read model lags its writes, so a fresh
 * map that still doesn't name the account keeps the optimistic entry rather than
 * hiding the button again. Never throws: the create already succeeded.
 */
function* reconcileV4Permissions(accountId) {
	try {
		// Unscoped for a manager: inside an entered manager the map would shrink to it.
		const user = yield select(selectCurrentUser());
		const unscoped = !!user?.isAccountManager;
		let newest = null;
		for (let attempt = 0; attempt < V4_LOOKUP_ATTEMPTS; attempt++) {
			if (attempt) yield delay(V4_LOOKUP_DELAY);
			const fresh = unscoped
				? yield call(getV4Permissions, v4Unscoped())
				: yield call(getV4Permissions);
			// A failed read is silence, not a denial.
			if (!fresh) continue;
			if (!hasV4AccountRules(fresh) || namesV4Account(fresh, accountId)) {
				yield* putV4Permissions(fresh);
				return;
			}
			newest = fresh;
		}
		// Still behind: take what else moved, keeping the grant we made.
		if (newest) yield* putV4Permissions(withV4AccountRules(newest, accountId, V4_ADMIN_RULES));
	} catch (e) {
		console.warn('Could not reconcile the V4 session permissions', e?.response?.data || e);
	}
}

// Create has no owner field, so the parent is the scope; read it live, as the interceptor does.
function v4CreateParent(user, account) {
	const parentId = account.get('v4OwnerId')
		|| (user?.isV4Native && user.isAccountManager ? user.v4RootAccountId : null);
	const atHome = !getV4SessionScope()?.accountId;
	if (!parentId || (atHome && user?.isOwnV4Account(parentId))) return {parentId, scope: undefined};
	return {parentId, scope: v4ScopeForUuid(parentId)};
}

const landedElsewhere = (created, parentId) => !!created?.owner_id && !!parentId
	&& String(created.owner_id).lc() !== String(parentId).lc();

function* updateV4Account(account, grantUserIds) {
	// Reported after `accountSaved` — the account itself really was created.
	let grantsMissed = false;
	// Set once the session has granted itself the new account.
	let reconcileAccountId = null;
	let pendingCheck = null;
	if (account.id) {
		// Update requires the full object — read first so the settings survive.
		const current = (yield v4api.accountsDetail(account.id))?.data;
		const settings = withV4AccountProfile({...current?.settings, ...account.get('v4Settings')}, {
			reference: account.get('v4Reference'),
			language: account.get('v4Language'),
			timezone: account.get('v4Timezone'),
		});
		// null = untouched: writing back a stale flag would undo a newer change.
		const wanted = account.get('v4ManagerFlag');
		if (wanted != null && !!wanted !== isV4ManagerFlagged(current?.settings)) {
			settings[V4_ACCOUNT_MANAGER_SETTING] = v4ManagerSettingValue(wanted);
		}
		// Only sent when changed.
		const billing = v4BillingUpdate(current?.billing, {
			customer: account.get('v4BillingCustomer'),
			interval: account.get('v4BillingInterval'),
		});
		// null = untouched: what is stored, never the resolved one the detail answers with.
		const whitelabel = account.get('v4Whitelabel') ?? (yield call(fetchStoredV4Whitelabel, account.id));
		yield v4api.accountsUpdate(account.id, {
			name: account.get('name'),
			whitelabel,
			settings,
			...(billing !== undefined ? {billing} : {}),
		});
	} else {
		const user = yield select(selectCurrentUser());
		const {parentId, scope} = v4CreateParent(user, account);
		// Lets the fallback lookup tell which row is new. Never fatal: failing
		// to list accounts must not stop an account from being created.
		let idsBefore = new Set();
		if (grantUserIds?.length || scope) {
			try {
				idsBefore = new Set((yield* listAllV4Accounts())
					.filter(a => a?.id).map(a => String(a.id).lc()));
			} catch (e) {
				console.warn('Could not snapshot the V4 accounts', e?.response?.data || e);
			}
		}
		const body = {name: account.get('name')};
		// Nothing stored yet: send the form's values.
		const settings = withV4AccountProfile(
			account.get('v4ManagerFlag') ? {[V4_ACCOUNT_MANAGER_SETTING]: v4ManagerSettingValue(true)} : {},
			{reference: account.get('reference'), language: account.get('language'), timezone: account.get('timezone')},
		);
		if (Object.keys(settings).length) body.settings = settings;
		// Left out, the new account inherits.
		if (account.get('v4Whitelabel')) body.whitelabel = account.get('v4Whitelabel');
		const res = scope
			? yield v4api.accountsCreate(body, scope)
			: yield v4api.accountsCreate(body);
		// undefined = not looked up yet, null = not found.
		let created;
		if (grantUserIds?.length) {
			created = yield* createdV4Account(res, idsBefore, account.get('name'));
			const newId = created?.id;
			if (newId) {
				const failed = yield* grantNewAccountPermissions(newId, grantUserIds);
				grantsMissed = failed.length > 0;
				// Opens the new account's gates now; settled at the end.
				if (yield* applyV4SessionGrant(newId, grantUserIds, failed)) {
					reconcileAccountId = newId;
				}
			} else {
				grantsMissed = true;
			}
		}
		if (scope) pendingCheck = {parentId, res, idsBefore, created};
	}
	yield put(setEditAccount(null));
	// AdminPage's loader owns the per-account name/device caches — go through it.
	yield put(getAccounts());
	yield put(enqueueSnackbar(getI18n().t('accountSaved')));
	if (pendingCheck) {
		const {parentId, res, idsBefore} = pendingCheck;
		const created = pendingCheck.created !== undefined
			? pendingCheck.created
			: yield* createdV4Account(res, idsBefore, account.get('name'));
		if (landedElsewhere(created, parentId)) {
			console.warn('The new V4 account was not created under the chosen account manager',
				{requested: parentId, owner: created.owner_id, id: created.id});
			yield put(enqueueSnackbar(getI18n().t('v4AccountCreatedElsewhere',
				'The account was created, but not under the chosen account manager.'), {variant: 'warning'}));
		}
	}
	// Last, so it is the message that stays: the account exists but the ticked
	// users cannot reach it yet.
	if (grantsMissed) {
		yield put(enqueueSnackbar(getI18n().t('somethingWentWrong'), {variant: 'error'}));
	}
	// Trails the UI on purpose — the optimistic grant is already in place, and
	// `takeLatest` cancelling this just leaves it standing.
	if (reconcileAccountId) yield* reconcileV4Permissions(reconcileAccountId);
}

function* updateAccount({account, v4UserIds}) {
	const currentUser = yield select(selectCurrentUser());
	if (currentUser?.isV4Native) {
		try {
			yield* updateV4Account(account, v4UserIds);
		} catch (e) {
			yield put(enqueueSnackbar(getI18n().t('somethingWentWrong'), { variant: 'error' }));
			yield put(updateAccountFailed());
			Sentry.captureException(e);
		}
		return;
	}
	try {
		if (account.id) {
			yield api.updateAccount(account);
		} else {
			const result = yield api.addAccount(account);
			account = new Account(result);
		}
	} catch (e) {
		yield put(enqueueSnackbar(getI18n().t('somethingWentWrong'), { variant: 'error' }));
		yield put(updateAccountFailed());
		Sentry.captureException(e);
		return;
	}
	yield put(setEditAccount(null));
	yield put(getProms());
	const newAccounts = yield api.getAccounts();
	yield put(accountsChanged(newAccounts));
	yield put(setSelectedAccount(account));
	yield put(enqueueSnackbar(getI18n().t('accountSaved')));
}

function* loginAccount({account, device, portal}) {
	const adminUser = yield select(selectCurrentUser());
	// No V4 impersonation endpoint — pin the scope and reload into it.
	// Sub-account managers boot their own shell; the root enters as a portal to reach its devices.
	if (adminUser?.isV4Native) {
		const accounts = yield select(selectAccounts());
		const tree = buildV4AccountTree(accounts, adminUser.v4RootAccountId);
		const manager = !portal
			&& tree.has(account.id) && !tree.isRoot(account.id) && tree.isManager(account.id);
		enterV4SessionScope(account.id, account.get('name'), {manager});
		window.location.href = manager ? '/' : (device ? `/devices/${device.id}` : '/');
		return;
	}
	const user = yield api.loginAccount(account.id, adminUser.get('token'), adminUser.isSuperAdmin ? 'adminUser' : 'accountManagerUser');
	localStorage.setItem('user', JSON.stringify(user.toJS()));
	window.location.href = device ? `/devices/${device.id}` : '/';
}

export const accountSagas = [
	takeEvery(LOGIN_ACCOUNT, loginAccount),
	takeLatest(UPDATE_ACCOUNT, updateAccount)
];